import React from "react";
import { BookOpen, FileText } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  FormField, MathText, NativeSelect, Textarea,
} from "../../ui";
import { QuizImageField } from "../QuizImageField";
import { CODE_LANGUAGES } from "./questionRules";

/**
 * Step 3 — what students read: the question, and whatever it needs beside it —
 * a picture, a code snippet for the Code reading type, or a passage several
 * questions share.
 */
export default function QuestionStep({ form, set, errors, passage, onPickPassage, onRemovePassage }) {
  const isImage = form.questionType === "image_mcq";
  const isCode = form.questionType === "code_conceptual";

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <FormField
            label="Question"
            required
            error={errors.questionText}
            hint="For maths, write $x^2$ inline or $$\int_0^1 x\,dx$$ on its own line."
          >
            <Textarea
              rows={4}
              value={form.questionText}
              onChange={(e) => set("questionText", e.target.value)}
              placeholder={isCode
                ? "e.g. What does this program print?"
                : "e.g. A shopkeeper sells an item at a 10% discount and still makes 26% profit. By what % is the marked price above cost?"}
              autoFocus={!form.questionText}
            />
          </FormField>

          {form.questionText.includes("$") && (
            <div className="tw-flex tw-flex-col tw-gap-1 tw-rounded-md tw-bg-muted/50 tw-px-3 tw-py-2">
              <span className="tw-text-xs tw-font-medium tw-text-content-muted">How the maths reads</span>
              <MathText text={form.questionText} className="tw-text-sm tw-text-content" />
            </div>
          )}

          <QuizImageField
            label={isImage ? "Question image" : "Image (optional)"}
            hint={isImage ? "The picture the question is about." : "A diagram, chart or photo shown with the question."}
            value={form.imageUrl}
            onChange={(url) => set("imageUrl", url)}
          />
        </AppCardContent>
      </AppCard>

      {isCode && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Code</AppCardTitle>
            <AppCardDescription>The snippet students read. They do not run it — they answer from the options.</AppCardDescription>
          </AppCardHeader>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            <FormField label="Language">
              <NativeSelect value={form.codeLanguage} onChange={(e) => set("codeLanguage", e.target.value)}>
                {CODE_LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Code snippet">
              <Textarea
                rows={8}
                className="tw-font-mono tw-text-[0.8rem]"
                value={form.codeSnippet}
                onChange={(e) => set("codeSnippet", e.target.value)}
                placeholder="// paste the code students need to read"
              />
            </FormField>
          </AppCardContent>
        </AppCard>
      )}

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Passage or case</AppCardTitle>
          <AppCardDescription>
            Optional. For a set of questions about one paragraph or document — reading comprehension, a medical
            coding case, a chart.
          </AppCardDescription>
        </AppCardHeader>
        <AppCardContent>
          {form.passage ? (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-rounded-md tw-border tw-border-solid tw-border-primary/40 tw-bg-primary/[0.05] tw-px-3 tw-py-2.5">
              {passage?.kind === "pdf"
                ? <FileText size={18} className="tw-shrink-0 tw-text-primary" aria-hidden="true" />
                : <BookOpen size={18} className="tw-shrink-0 tw-text-primary" aria-hidden="true" />}
              <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content">{passage?.title || "Loading…"}</span>
                <span className="tw-text-xs tw-text-content-muted">
                  {passage?.kind === "pdf" ? "Document (PDF)" : "Passage"} — shown beside this question in the exam
                </span>
              </span>
              <AppButton type="button" variant="outline" size="sm" onClick={onPickPassage}>Change</AppButton>
              <AppButton type="button" variant="ghost-destructive" size="sm" onClick={onRemovePassage}>Remove</AppButton>
            </div>
          ) : (
            <AppButton type="button" variant="outline" size="sm" onClick={onPickPassage}>
              <BookOpen size={16} aria-hidden="true" /> Attach a passage or document
            </AppButton>
          )}
        </AppCardContent>
      </AppCard>
    </div>
  );
}
